import { createContext, useRef, useState, useContext } from "react";
import { PhaserGame } from "./PhaserGame";
import { config as gameConfig } from "../game-config";
import { LocationBar } from "../components/LocationBar.tsx";
import StoryDialog from "../components/StoryDialog.tsx";
import { Box } from "@radix-ui/themes";
import { Countdown } from "../components/Countdown.tsx";
import ChallengeDialog from "../components/ChallengeDialog.tsx";
import FinalAnswersWidget from "../components/FinalAnswersWidget.tsx";
import CipherVictory from "../components/CipherVictory.tsx";
import { GreaseTheWheels } from "../components/GreaseTheWheels.tsx";
import Music from "../components/Music.tsx";
import GameIntro from "../components/GameIntro.tsx";
import MonsterIntro from "../components/MonsterIntro.tsx";
import GameOutro from "../components/GameOutro.tsx";
import ChatDialog from "../components/ChatDialog.tsx";
import { WebSocketContext, AccountContext } from "../App.tsx";
import { Link, Navigate } from "react-router-dom";

export interface IRefPhaserGame {
  game: Phaser.Game | null;
  scene: Phaser.Scene | null;
}

interface ZoneContextInterface {
  currentZone?: string;
}

interface StoryContextInterface {
  storyTitle?: string;
  storyText?: string;
  storyDialogOpen?: boolean;
}

export const ZoneContext = createContext<
  [ZoneContextInterface, (_: ZoneContextInterface) => void]
>([{}, (_) => {}]);

export const StoryContext = createContext<
  [StoryContextInterface, (_: StoryContextInterface) => void]
>([{ storyDialogOpen: true }, (_) => {}]);

function Game() {
  const [account] = useContext(AccountContext);

  // Redirect to /account if no account exists
  if (!account) {
    return <Navigate to="/account" />;
  }

  // No Navbar in-game: the site chrome distracts (and steals clicks) during
  // play. A reload of /game bounces to /account anyway, which has the nav.
  // The stylized R (bottom-center, clear of the corner widgets) is the one
  // way home — a client-side Link, so the websocket stays open.
  return (
    <Box>
      <style>{`
        .ro-home-mark{position:fixed;bottom:10px;left:50%;
          transform:translateX(-50%);z-index:1000;
          width:38px;height:38px;display:flex;align-items:center;
          justify-content:center;border-radius:50%;background:black;
          border:1px solid var(--ro-accent);color:var(--ro-accent);
          font-family:'MelGame','Courier New',monospace;font-size:22px;
          font-weight:700;text-decoration:none;opacity:0.7;
          text-shadow:0 0 8px rgba(31,168,224,0.7);}
        .ro-home-mark:hover{opacity:1;
          box-shadow:0 0 14px rgba(31,168,224,0.8);}
      `}</style>
      <Link to="/" className="ro-home-mark" title="REALI7Y OVERRUN — home">
        R
      </Link>
      <Countdown />
      <InnerGame />
    </Box>
  );
}

function InnerGame() {
  //  References to the PhaserGame component (game and scene are exposed)
  const phaserRef = useRef<IRefPhaserGame | null>(null);
  const [, setLocalScene] = useState<Phaser.Scene | null>(null);
  const [currentSceneName, setCurrentSceneName] = useState<string>("");
  const [storyContext, setStoryContext] = useState({});
  const [zoneContext, setZoneContext] = useState({});
  const websocket = useContext(WebSocketContext);

  // Event emitted from the PhaserGame component
  const currentScene = (scene: Phaser.Scene) => {
    setLocalScene(scene);
    setCurrentSceneName(scene.scene.key);
  };

  return (
    <div id="game-app">
      <ZoneContext.Provider value={[zoneContext, setZoneContext]}>
        <StoryContext.Provider value={[storyContext, setStoryContext]}>
          <LocationBar />
          <StoryDialog />
          <ChallengeDialog />
          <FinalAnswersWidget />
          <CipherVictory />
          <GreaseTheWheels />
          <Music />
          <GameIntro />
          <MonsterIntro />
          <GameOutro />
          {currentSceneName === "GameMain" && websocket != null && (
            <ChatDialog websocket={websocket} />
          )}
          {/*
            <GlitchTimerOverlay
              startSeconds={900}
              onExpire={() => {
                localScene?.scene.start("GameLost");
                console.log("Expired");
              }}
              onTick={(elapsedSeconds) => {
                const speedUpFactor = 1; // Speed up factor for glitch level
                const level =
                  (Math.floor((elapsedSeconds * speedUpFactor) / 60) + 1) % 15;
                console.log(`Level: ${level} tick`);
                if (localScene != null) {
                  (localScene as GameMain).applyGlitchLevel(level);
                }
              }}
            />
          */}
          <PhaserGame
            ref={phaserRef}
            currentActiveScene={currentScene}
            config={gameConfig}
          />
        </StoryContext.Provider>
      </ZoneContext.Provider>
    </div>
  );
}

export { Game };
